import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { submitLead } from "../../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { useI18n } from "../../i18n/I18nContext";

export const DemoModal = ({ open, onOpenChange }) => {
  const { lang, t } = useI18n();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    gpu_count: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = (val) => {
    if (!val) setTimeout(() => { setDone(false); setLoading(false); }, 300);
    onOpenChange(val);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error(lang === "en" ? "Required fields missing" : "Заполните обязательные поля", {
        description: lang === "en" ? "Name, company, email are required" : "Имя, компания, email",
      });
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, source: "demo_form" });
      setDone(true);
      toast.success(lang === "en" ? "Request submitted" : "Заявка отправлена", {
        description: lang === "en" ? "We will reach out within 24 hours" : "Свяжемся в течение 24 часов",
      });
    } catch (err) {
      toast.error(lang === "en" ? "Submission failed" : "Не удалось отправить", {
        description: lang === "en" ? "Please try again or email us directly" : "Попробуйте ещё раз или напишите на email",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="demo-modal"
        className="bg-ink-surface border border-ink-text/10 text-ink-text max-w-xl p-0 overflow-hidden"
      >
        <div className="p-7 sm:p-9">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight text-2xl">
              {t.demoModal.title}
            </DialogTitle>
            <DialogDescription className="text-muted mono text-xs uppercase tracking-widest mt-1">
              {t.demoModal.subtitle}
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="mt-8 text-center py-8" data-testid="demo-success">
              <div className="inline-flex w-14 h-14 rounded-full bg-success/10 border border-success/40 items-center justify-center mb-5">
                <CheckCircle size={28} weight="duotone" className="text-success" />
              </div>
              <div className="font-heading text-2xl font-bold mb-2">
                {t.demoModal.successTitle}
              </div>
              <p className="text-sm text-muted max-w-sm mx-auto leading-relaxed">
                {t.demoModal.successDesc}
              </p>
              <button
                onClick={() => close(false)}
                className="mt-7 btn-secondary rounded-lg px-5 py-2.5 font-semibold"
              >
                {t.demoModal.btnClose}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field
                  label={t.demoModal.fieldName}
                  value={form.name}
                  onChange={(v) => set("name", v)}
                  testid="demo-name"
                />
                <Field
                  label={t.demoModal.fieldCompany}
                  value={form.company}
                  onChange={(v) => set("company", v)}
                  testid="demo-company"
                />
                <Field
                  label={t.demoModal.fieldEmail}
                  type="email"
                  value={form.email}
                  onChange={(v) => set("email", v)}
                  testid="demo-email"
                />
                <Field
                  label={t.demoModal.fieldPhone}
                  value={form.phone}
                  onChange={(v) => set("phone", v)}
                  testid="demo-phone"
                />
                <div className="sm:col-span-2">
                  <Field
                    label={t.demoModal.fieldGpu}
                    value={form.gpu_count}
                    onChange={(v) => set("gpu_count", v)}
                    testid="demo-gpu"
                    placeholder={t.demoModal.fieldGpuPlaceholder}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block">
                    <div className="mono text-[10px] uppercase tracking-widest text-muted mb-1.5">
                      {t.demoModal.fieldMessage}
                    </div>
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      data-testid="demo-message"
                      placeholder={t.demoModal.fieldMessagePlaceholder}
                      className="w-full rounded-lg bg-ink border border-ink-text/10 px-3.5 py-2.5 text-sm outline-none focus:border-brand-cyan transition placeholder:text-muted-deep resize-none"
                    />
                  </label>
                </div>
              </div>

              <div className="pt-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-muted mono">
                  <ShieldCheck size={14} className="text-brand-cyan" /> NDA protected
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="demo-submit"
                  className="btn-primary rounded-lg px-6 py-2.5 font-semibold inline-flex items-center gap-2"
                >
                  <PaperPlaneTilt size={16} weight="bold" />
                  {loading ? t.demoModal.btnSubmitting : t.demoModal.btnSubmit}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value, onChange, type = "text", testid, placeholder = "" }) => (
  <label className="block">
    <div className="mono text-[10px] uppercase tracking-widest text-muted mb-1.5">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      placeholder={placeholder}
      className="w-full rounded-lg bg-ink border border-ink-text/10 px-3.5 py-2 text-sm outline-none focus:border-brand-cyan transition placeholder:text-muted-deep"
    />
  </label>
);

export default DemoModal;
